import type { PrimitiveDef, SubstrateManifest } from '../manifest';
import type { BenchGraph, RunRecord } from '../runtime';

function primitiveById(manifest: SubstrateManifest, id: string): PrimitiveDef | undefined {
  return manifest.primitives.find((primitive) => primitive.id === id);
}

function tierColor(manifest: SubstrateManifest, tierId: string): string {
  return manifest.tier_labels.find((tier) => tier.id === tierId)?.color ?? 'var(--sp-accent)';
}

function nodeStatus(run: RunRecord | null, nodeId: string): string {
  return run?.nodes.find((node) => node.nodeId === nodeId)?.status ?? 'idle';
}

export function ComposerCanvas({
  manifest,
  graph,
  currentRun,
  selectedNodeId,
  onSelectNode,
  onRun,
  onSaveRecipe,
  onResetRecipe,
}: {
  manifest: SubstrateManifest;
  graph: BenchGraph;
  currentRun: RunRecord | null;
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string) => void;
  onRun: () => void;
  onSaveRecipe: () => void;
  onResetRecipe: () => void;
}) {
  return (
    <section className="sp-widget" style={{ minHeight: 0 }}>
      <div className="sp-widget-header">
        <span>Composer Canvas</span>
        <span className="mono">{currentRun ? `run=${currentRun.status}` : 'local V1 graph'}</span>
      </div>
      <div
        className="sp-widget-body"
        style={{
          background:
            'linear-gradient(90deg, rgba(127,209,185,0.04) 1px, transparent 1px), linear-gradient(rgba(127,209,185,0.04) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          border: '1px solid var(--sp-border)',
          borderRadius: 'var(--sp-radius-lg)',
          display: 'grid',
          gap: 16,
          minHeight: 520,
          overflow: 'auto',
          padding: 24,
        }}
      >
        <div style={{ alignItems: 'start', display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <p className="mono" style={{ color: 'var(--sp-accent)', fontSize: 12, letterSpacing: '0.08em', margin: 0 }}>
              SUBSTRATE COMPOSITION
            </p>
            <h2 style={{ color: 'var(--sp-text)', fontSize: 22, margin: '8px 0' }}>{graph.label}</h2>
            <p style={{ color: 'var(--sp-text-muted)', maxWidth: 720, margin: 0 }}>
              Click nodes to edit manifest-defined parameters. Run pauses at approval gates and resumes only after
              an explicit approval decision.
            </p>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
            <button className="sp-btn sp-btn-primary" onClick={onRun} type="button">
              Run
            </button>
            <button className="sp-btn" onClick={onSaveRecipe} type="button">
              Save recipe
            </button>
            <button className="sp-btn" onClick={onResetRecipe} type="button">
              Reset chain
            </button>
          </div>
        </div>

        <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {graph.nodes.map((node, index) => {
            const primitive = primitiveById(manifest, node.primitiveId);
            const color = tierColor(manifest, primitive?.tier ?? '');
            const status = nodeStatus(currentRun, node.id);
            const selected = selectedNodeId === node.id;
            return (
              <div key={node.id} style={{ alignItems: 'center', display: 'flex', gap: 10 }}>
                <button
                  onClick={() => onSelectNode(node.id)}
                  style={{
                    background: selected ? 'rgba(127,209,185,0.14)' : 'rgba(11,13,16,0.88)',
                    border: `1px solid ${selected ? 'var(--sp-accent)' : `${color}66`}`,
                    borderTop: `3px solid ${color}`,
                    borderRadius: 'var(--sp-radius-lg)',
                    cursor: 'pointer',
                    minHeight: 116,
                    minWidth: 180,
                    padding: 12,
                    textAlign: 'left',
                  }}
                  type="button"
                >
                  <div style={{ color, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                    {primitive?.capability_class ?? 'primitive'}
                  </div>
                  <strong style={{ color: 'var(--sp-text)', display: 'block', marginTop: 4 }}>{node.label}</strong>
                  <code style={{ color: 'var(--sp-text-dim)', display: 'block', fontSize: 11, marginTop: 8 }}>
                    {node.primitiveId}
                  </code>
                  <span
                    className="mono"
                    style={{
                      color: status === 'paused' ? 'var(--sp-warn)' : status === 'completed' ? 'var(--sp-ok)' : 'var(--sp-text-dim)',
                      display: 'block',
                      fontSize: 10,
                      marginTop: 8,
                      textTransform: 'uppercase',
                    }}
                  >
                    {status}
                  </span>
                </button>
                {index < graph.nodes.length - 1 ? (
                  <span className="mono" style={{ color: 'var(--sp-text-dim)' }}>
                    -&gt;
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
